import { Check, ListFilter } from 'lucide-react';
import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FILTER_SORT_BY, FILTER_STATUS } from '@/utils/constants';
import { SEARCH_PARAM_ROUTE_TYPE, SEARCH_PARAM_SORT_BY } from '@/utils/constants/queries';

interface RouteSortingProps {
  isToday?: boolean;
}

const RouteSorting: FC<RouteSortingProps> = ({ isToday }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSortType =
    searchParams.get(SEARCH_PARAM_SORT_BY) ||
    (isToday ? FILTER_SORT_BY.DEVIATION : FILTER_SORT_BY.LINE_NUMBER);
  const routeSortType = searchParams.get(SEARCH_PARAM_ROUTE_TYPE) ?? '';
  const shouldDisableDeviation =
    !isToday || [FILTER_STATUS.INACTIVE, FILTER_STATUS.COMPLETED].includes(routeSortType);

  const sortingOptions = [
    {
      value: FILTER_SORT_BY.LINE_NUMBER,
      label: t('filters.line_number'),
    },
    {
      value: FILTER_SORT_BY.TRAIN_NUMBER,
      label: t('filters.train_number'),
    },
    {
      value: FILTER_SORT_BY.DEVIATION,
      label: t('filters.deviation'),
    },
  ];

  const selectedRouteTypeLabel =
    sortingOptions.find((type) => type.value === selectedSortType)?.label ||
    sortingOptions[0].label;

  const onChangeSortType = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set(SEARCH_PARAM_SORT_BY, value);

        return newParams;
      });
    },
    [searchParams]
  );

  useEffect(() => {
    if (shouldDisableDeviation && selectedSortType === FILTER_SORT_BY.DEVIATION) {
      onChangeSortType(FILTER_SORT_BY.LINE_NUMBER);
    }
  }, [selectedSortType, shouldDisableDeviation]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 cursor-pointer border-0 bg-secondary rounded-full px-4 py-2 font-medium text-sm">
        <ListFilter className="size-5" absoluteStrokeWidth />
        <span className="w-max">{selectedRouteTypeLabel}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-29!" align="start" avoidCollisions>
        {sortingOptions.map(({ label, value }) => {
          const isMenuItemDisabled = shouldDisableDeviation && value === 'deviation';

          return (
            <DropdownMenuItem
              disabled={isMenuItemDisabled}
              key={value}
              onClick={() => onChangeSortType(value)}
            >
              <span>{label}</span>
              {selectedSortType === value && <Check className="ml-auto" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RouteSorting;
